// Shared admin UI primitives: StatusBadge, Modal, ConfirmDialog, EmptyState.
import { useEffect } from "react";
import { X } from "lucide-react";

export function StatusBadge({ status }) {
  const map = {
    new: "bg-brand-yellow/15 text-brand-yellow",
    contacted: "bg-brand-lavender/15 text-brand-lavender",
    in_progress: "bg-brand-blue/15 text-brand-blue",
    won: "bg-brand-green/15 text-brand-green",
    lost: "bg-brand-red/15 text-brand-red",
    draft: "bg-white/8 text-brand-gray",
    published: "bg-brand-green/15 text-brand-green",
  };
  const cls = map[status] || "bg-white/10 text-brand-white";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-sm px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] font-medium ${cls}`}
    >
      {String(status || "—").replace("_", " ")}
    </span>
  );
}

export function Modal({ open, onClose, children, title, wide = false }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
      <div
        onClick={(e) => e.target === e.currentTarget && onClose?.()}
        className="absolute inset-0"
      />
      <div
        className={`relative admin-card rounded-sm bg-ink-charcoal w-full ${wide ? "max-w-4xl" : "max-w-lg"} max-h-[85vh] overflow-y-auto admin-scroll shadow-2xl`}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between p-5 md:p-6 border-b border-white/8">
          <p className="eyebrow-muted">[ {title || "Details"} ]</p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8 grid place-items-center rounded-sm text-brand-gray hover:text-brand-yellow border border-white/10"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 md:p-6">{children}</div>
      </div>
    </div>
  );
}

export function ConfirmDialog({ open, onCancel, onConfirm, title, description, confirmLabel = "Delete", danger = true }) {
  return (
    <Modal open={open} onClose={onCancel} title={title || "Confirm"}>
      <p className="body-text">{description}</p>
      <div className="mt-6 flex items-center justify-end gap-3">
        <button type="button" onClick={onCancel} className="btn-outline">
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className={`inline-flex items-center gap-2 px-4 py-3 text-[11px] uppercase tracking-[0.14em] font-medium rounded-sm transition-colors ${
            danger ? "bg-brand-red text-white hover:bg-brand-red/85" : "btn-primary"
          }`}
        >
          {confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

export function EmptyState({ title, description, action }) {
  return (
    <div className="border border-dashed border-white/10 rounded-sm p-10 text-center">
      <p className="eyebrow-muted justify-center">[ Nothing here yet ]</p>
      <h3 className="text-xl font-semibold mt-3">{title}</h3>
      {description && <p className="body-text mt-2 max-w-md mx-auto text-sm">{description}</p>}
      {action && <div className="mt-6 inline-flex">{action}</div>}
    </div>
  );
}

export function FieldLabel({ children }) {
  return <span className="eyebrow-muted block mb-2">{children}</span>;
}

export function AdminInput(props) {
  return (
    <input
      {...props}
      className={`w-full bg-white/[0.03] border border-white/12 rounded-sm px-3 py-2.5 text-sm outline-none focus:border-brand-yellow transition-colors font-body ${props.className || ""}`}
    />
  );
}

export function AdminTextarea(props) {
  return (
    <textarea
      {...props}
      className={`w-full bg-white/[0.03] border border-white/12 rounded-sm px-3 py-2.5 text-sm outline-none focus:border-brand-yellow transition-colors font-body min-h-28 resize-y ${props.className || ""}`}
    />
  );
}

export function AdminSelect(props) {
  return (
    <select
      {...props}
      className={`w-full bg-white/[0.03] border border-white/12 rounded-sm px-3 py-2.5 text-sm outline-none focus:border-brand-yellow transition-colors font-body ${props.className || ""}`}
    >
      {props.children}
    </select>
  );
}
